/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import {
  TextureLoader, FileLoader, Mesh, Object3D,
  VideoTexture, LinearFilter, RGBFormat, Material, DoubleSide,
} from 'three';
import {
  Director,
  Stage,
  Solids,
  Materials,
  MathFunctions,
  CurtainRod,
  MoveActor,
  RenderConfig,
  DefaultCaps,
  StageDesc,
  CapNames,
} from '@sick-davinci/glow';
import {
  CrownViewCapData,
  CrownPointCloudCap,
  CrownShapeCap,
  CrownPointCloudDecorationDesc,
} from '@sick-davinci/glow-crown';
import {
  MsgPackDecoder,
  CrownProtocolBridge,
  CrownConnectionProxy,
} from '@sick-davinci/device-communication';

interface TriggerEvent {
  msgpack?: string;
  json?: string;
  mp4?: string;
  img?: string;
  tstamp?: string;
}

const msgPackDecoder = new MsgPackDecoder();

/**
 * Demo:    SafetyAnalyticsSPS
 * Description:
 *  This demo application shows an elaborate application which was shown at SPS Drives 2018.
 *  There are two application parts:
 *   1. Read event-data (CrownView events) from the web-server and show it on an invisible viewport.
 *      Take screenshots of this viewport
 *   2. Show a Carousel (cover-flow) which displays the screenshots in a nice way.
 *      The user can use touch or mouse gestures to switch between the screenshots and start the linked video.
 * Features:
 *  - Orbit navigation with custom settings
 *  - Capability handling
 *  - CrownView
 *  - Carousel
 *  - Display videos
 *  - Screenshots
 *  - WebServer interaction
 *  - Actors
 *  - Navitve threejs calls
 */
export const run = ((viewParent: HTMLElement, carousel: Carousel, eventUrl: string) => {

  // ** setup screen *
  const setup: StageDesc = {
    cameras: [{
      name: 'default',
      from: [470, -950, 8000],
      to: [470, 50, 0],
      up: [0, 0, 1],
      fov: 10,
    }],
    scenes: [
      {
        name: 'default',
      },
    ],
    viewports: [
      {
        scene: 'default',
        name: 'defaultViewport',
        camera: 'default',
        left: 0,
        top: 0,
        width: 1.0,
        height: 1.0,
        navigator: 'default',
        bgcolor: 0xbbbbbb,
      },
    ],
  };

  const stage = new Stage(viewParent, setup);
  stage.start();

  // ** data handling using capabilities *
  const director = new Director(stage);
  DefaultCaps.init(director);
  director.addCapability(new CrownPointCloudCap());
  director.addCapability(new CrownShapeCap());

  // ** Connect to the device via the ViewCrown using the provided ip and view-id
  const cp = new CrownProtocolBridge();
  const cc = new CrownConnectionProxy((data) => {
    if (!!data && !!data.header && !!data.header.function) {
      const func = data.header.function;
      switch (func) {
        case 'View/Present/create': // simulate registration (called by CrownView)
          cc.putObj({ header: { status: 0 }, data: { handle: { type: 'handle', id: 0 } } });
          break;
        case 'View/Present/setID':
          cc.putObj({ header: { status: 0 } });
          break;
        case 'View/Present/register':
          cc.putObj({ header: { status: 0 }, data: { success: true } });
          break;
        default: break;
      }
    }
  });

  const view = new CrownViewCapData(cc, cp, 'viewer3D');

  // ** Set all necessary callback functions
  view.onPresentStart(() => {
    stage.lockRenderer();
    stage.clearAllScenes();
  });
  view.onPresentEnd(() => stage.unlockRenderer());
  view.onPresentClear(() => stage.clearAllScenes());
  view.onPresentRemove((id) => stage.clearObject(stage.getObjectIdByName(id)));
  view.onPresentAddCapData((capData) => {
    switch (capData.payload.Type) {
      case CrownPointCloudCap.className:
        (capData.payload.Decorations[0].data as CrownPointCloudDecorationDesc).PointSize *= 3;
        break;
      default: break;
    }

    director.handleCapData(capData); // put shape into the scene
  });

  let eventMaxNum = 0;
  // ** Put in data from the server once the connection is established
  view.onConnectionChanged((connected) => {
    if (connected) {
      handleEvents();
    }
  });

  let firstScan = true;
  function handleEvents() {
    getEventsByFile(eventUrl, async (events) => {
      const keys = Array.from(events.keys());
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const ev = events.get(key) as TriggerEvent;
        if (key > eventMaxNum) { // only add new events (list must be sorted!)
          if (Object.keys(ev).length !== 3) {
            // remove non tripples
            events.delete(key);
          } else {
            eventMaxNum = key;
            // capture an image from msgpack entry
            const url = await loadImgFile(ev.msgpack);
            ev.img = url;
            const tstamp = await loadTStamp(ev.json);
            ev.tstamp = tstamp;
            // console.log(ev);
            carousel.addEvent(ev);
          }
        }
        if (i === keys.length - 1) {
          if (firstScan) {
            firstScan = false;
            carousel.moveToLast();
            Carousel.ROT_DURATION = 0.4;  // set from 0 (initial) to 0.4
          }
          setTimeout(() => {
            handleEvents();
          }, 3000); // check every 3s for new events
        }
      }
    });
  }

  /*
    let take = false;
    stage.setPostRenderHook(()=>{
      if(take) {
        stage.takeScreenshot("image/jpeg");
        take= false;
      }
    });*/
  // let i=0;

  const dataLoader = new FileLoader();
  dataLoader.setResponseType('arraybuffer');

  async function loadImgFile(path: string): Promise<string> {
    return new Promise<string>((res) => {
      dataLoader.setResponseType('arraybuffer');
      dataLoader.load(path, (data) => {
        const viewObjectRaw = msgPackDecoder.decode(data as any);
        const viewObject = new Array<any>();
        (viewObjectRaw as any).forEach((data: any) => {
          viewObject.push({ data, type: 'object' });
        });
        const obj = { header: { status: 0 }, data: { viewObject } };
        cc.putObj(obj);
        const dataURL = stage.takeScreenshot('image/jpeg');
        res(dataURL);
        // window["img"+i++] = stage.takeScreenshot("image/jpeg");
        // take = true;
      });
    });
  }

  function loadTStamp(path: string): Promise<string> {
    return new Promise<string>((res) => {
      dataLoader.setResponseType('json');
      dataLoader.load(path, (obj) => {
        const _obj = (obj as any);
        if (!!_obj.summary && !!_obj.summary.beginOfEvent) {
          res(_obj.summary.beginOfEvent);
        }
      });
    });
  }

  const parser = new DOMParser();
  const request = new XMLHttpRequest();

  // ** get events by listing the given folder (path)
  function getEvents(path: string, onEvents: (events: Map<number, any>) => void) {

    request.open('get', path, true);
    request.send();

    request.onreadystatechange = () => {
      if (request.readyState === 4) {
        const xmlDoc = parser.parseFromString(request.responseText, 'text/html');
        const hrefs = Array.from(xmlDoc.getElementsByTagName('a'));

        const events = new Map<number, any>();
        hrefs.forEach((it) => {
          const title = it.href.substr(it.href.lastIndexOf('/') + 1);
          const no = parseInt(title.substr(0, 4), 10);
          if (!isNaN(no) && !!it.href) {
            const ext = title.substr(title.indexOf('.') + 1);
            if (!!ext && ext.length > 0) {
              let obj = events.get(no);
              if (!obj) {
                obj = {};
              }
              const href = path + title; // handle absolute and relative paths alike
              obj[ext] = href;
              events.set(no, obj);
            }
          }
        });
        onEvents(events);
      }
    };
  }
});

const indexLoader = new FileLoader();
// ** get events by listing the given folder (path)
function getEventsByFile(path: string, onEvents: (events: Map<number, any>) => void) {
  interface Events {
    events: [
      {
        video: string,
        meta: string,
        viewdata: string,
      }
    ];
  }

  const basePath = './resources/events/';

  indexLoader.load(path, (json) => {
    json = JSON.parse(json as string);
    const evt = json as unknown as Events;
    const events = new Map<number, TriggerEvent>();
    evt.events.forEach( (e, i) => {
        const ev: TriggerEvent = {};
        ev.msgpack = basePath + e.viewdata;
        ev.json = basePath + e.meta;
        ev.mp4 = basePath + e.video;
        events.set(i, ev);
      });
    onEvents(events);
  });
}

/**
 * The carousel *
 */
export class Carousel {
  static readonly CAM_SELECTION = [0, 0, 6];
  static readonly CAM_VIDEO = [0, 0, 5.4];
  static ROT_DURATION = 0;  // no duration at the beginning, so switching to last element is without animation

  private _stage: Stage;
  private _textureLoader = new TextureLoader();
  private _coverList = new Array<Object3D>();
  private _rod: CurtainRod;
  private _videoPlane: Mesh;
  private _videoBackPlane: Mesh;
  private _index = 0;
  private _pos = 0;
  private _isPlaying = false;

  constructor(private _parentElem: HTMLElement, private _videoElement: HTMLVideoElement, private _info: HTMLElement, z1: number, z2: number) {
    this._rod = this._createCoverFlow(this._coverList, z1, z2);
    this._initStage(_parentElem);
    this._initCarousel();
  }

  addEvent(evt: any) {
    const texture = this._textureLoader.load(evt.img);
    const plane = new Mesh(Solids.createRectangle(0.8, 0.6), Materials.create({ type: 'standard', params: { map: texture, transparent: true, side: DoubleSide } }));
    plane.castShadow = true;
    this._coverList.push(plane);

    this._stage.applyThreeDirect((scene) => {
      scene.add(plane);
    });

    this._rod.setFractionalIndex(this._index);

    plane.userData.videoSrc = evt.mp4;
    plane.userData.tstamp = evt.tstamp;
  }

  moveAbit(offset: number) {
    this._rod.setFractionalIndex(this._pos + offset);
  }

  moveFinal(offset: number) {
    if (this._coverList.length > 0) {
      this._pos = this._pos + offset;
      this._alterIndex((offset < 0) ? -1 : 1);
      this._slideTo(this._index);
    }
  }

  moveToLast() {
    if (this._coverList.length > 0) {
      this.stop();
      this._index = this._coverList.length - 1;
      this._slideTo(this._index);
      this._parentElem.style.removeProperty('display');
      document.getElementById('loading').style.setProperty('display', 'none');
    }
  }

  prev() {
    if (this._coverList.length > 0) {
      this.stop();
      this._slideTo(this._alterIndex(-1));
    }
  }

  next() {
    if (this._coverList.length > 0) {
      this.stop();
      this._slideTo(this._alterIndex(1));
    }
  }

  play() {
    if (this._coverList.length > 0) {
      this._videoBackPlane.visible = true;
      ((this._coverList[this._index] as Mesh).material as Material).opacity = 0;
      this._videoElement.src = this._coverList[this._index].userData.videoSrc;
      this._videoElement.addEventListener('timeupdate', this._onPlay);
      this._moveCamTo(Carousel.CAM_VIDEO);
      this._isPlaying = true;
      this._videoElement.play();
    }
  }

  stop() {
    if (this._coverList.length > 0) {
      this._videoPlane.visible = false;
      this._videoBackPlane.visible = false; // just be sure
      ((this._coverList[this._index] as Mesh).material as Material).opacity = 1;
      this._videoElement.pause();
      this._moveCamTo(Carousel.CAM_SELECTION);
      this._isPlaying = false;
    }
  }

  isPlaying() {
    return this._isPlaying;
  }

  private _onPlay = () => {
    if (this._videoElement.duration > 0) {
      this._videoPlane.visible = true;
      this._videoBackPlane.visible = false;
      this._videoElement.removeEventListener('timeupdate', this._onPlay);
    }
  }

  private _createCoverFlow(objList: Array<Object3D>, z1: number, z2: number): CurtainRod {
    const rod = new CurtainRod(objList);
    rod.addTrack('x', (x: number) => (x - 0.5) * -4.5, (obj: Object3D, x: number) => obj.position.x = x);
    rod.addTrack('z', (z: number) => z1 * Math.exp(z2 * Math.pow(z - 0.5, 2.0)), (obj: Object3D, x: number) => obj.position.z = x);
    rod.addTrack('alpha', (a: number) => (a - 0.5) * Math.PI, (obj: Object3D, x: number) => obj.rotation.y = x);
    // rod.addTrack('transparency', (a:number, )=>Math.exp(-20*Math.pow(a-0.5,2.0)), (obj:Object3D, x:number)=>((obj as Mesh).material as Material).opacity = x);
    return rod;
  }

  private _initStage(elem: HTMLElement) {
    // ** basic setup */
    this._stage = new Stage(elem, {
      cameras: [{
        name: 'default', from: Carousel.CAM_SELECTION, to: [0, 0, 0], up: [0, 1, 0], fov: 20,
      }],
      scenes: [{ name: 'default' }],
      viewports: [{
        scene: 'default', camera: 'default', left: 0, top: 0, width: 1.0, height: 1.0,
        bgcolor: 0xffffff, // sick blue
      }],
    }, new RenderConfig({ enableShadows: true }));

    const director = new Director(this._stage);
    DefaultCaps.init(director);
    director.handle(CapNames.LIGHT, { payload: { lightType: 'spot', castShadow: true, color: 0xffffff }, orientation: { position: [0, 20, 30] } });
    director.handle(CapNames.LIGHT, { payload: { lightType: 'ambient', color: 0xffffff } });
    this._stage.start();
  }

  private _initCarousel() {
    // ** add video *
    const vtexture = new VideoTexture(this._videoElement);
    vtexture.minFilter = LinearFilter;
    vtexture.magFilter = LinearFilter;
    vtexture.format = RGBFormat;

    // (this._videoPlane.material as Material).opacity = 0;
    this._videoPlane = new Mesh(Solids.createRectangle(0.8, 0.6), Materials.create({ type: 'standard', params: { map: vtexture, transparent: true } }));
    this._videoPlane.visible = false;
    this._videoPlane.position.z = 3.59;
    this._stage.applyThreeDirect((scene) => {
      scene.add(this._videoPlane);
    });

    // * add loading plane *
    const texture = this._textureLoader.load('./images/loading.jpg');
    this._videoBackPlane = new Mesh(Solids.createRectangle(0.8, 0.6), Materials.create({ type: 'standard', params: { map: texture, transparent: true } }));
    this._videoBackPlane.position.z = this._videoPlane.position.z;
    this._videoPlane.visible = false;
    this._stage.applyThreeDirect((scene) => {
      scene.add(this._videoBackPlane);
    });

    // * add shadow plane *
    const floor = new Mesh(Solids.createRectangle(10, 10), Materials.create({ type: 'standard', params: { color: 0xFFFFFF } }));
    this._stage.applyThreeDirect((scene) => {
      scene.add(floor);
    });
    floor.rotateX(-Math.PI / 2.0);
    floor.translateZ(-0.4);
    floor.receiveShadow = true;

    this._rod.setSFunction((x) => MathFunctions.sigmoid(5 * (x - 0.5)));
  }

  private _alterIndex(offset: number): number {
    const newIdx = this._index + offset;
    this._index = Math.max(Math.min(newIdx, this._coverList.length - 1), 0);
    return this._index;
  }

  private _moveCamTo(pos: Array<number>) {
    this._stage.applyThreeDirect(
      (scene) => {
        const cam = this._stage.getCamera('default');
        const ret = new MoveActor(scene.getStage(), cam, 'cammove');
        ret.setDuration(0.2);
        ret.addTarget('position', 'vector3', cam.position, pos);
        this._stage.addActor(ret);
      },
    );
  }

  private _slideTo(index: number): void {
    this._stage.applyThreeDirect(
      (scene) => {
        const ret = new MoveActor(scene.getStage(), this._rod, 'crmove');
        ret.setDuration(Carousel.ROT_DURATION);
        ret.addTarget('index', 'number', this._pos, index);
        this._stage.addActor(ret);
      },
    );
    this._pos = index;
    this._info.innerHTML = this._coverList[index].userData.tstamp;
  }
}
